/**
 * Course overview page.
 */
import React, { useEffect, useState } from "react";
import { Box, Grid, Tab, Tabs, Divider, IconButton, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import MuiDrawer from "@mui/material/Drawer";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import { VText, VButton } from "form";
import CourseProgram from "./course.program";
import CourseComments from "./course.comment";
import CourseDownload from "./course.download";
import CourseBonus from "./course.bonus";
import CourseReplay from "./course.replay";
import CourseSidebar from "./course.sidebar";
import { useAsterController } from "context";
import { actionAppCourseDetailGet, actionMiniSidenav, actionPreviewModeSet } from "context/action";
import { shouldFetchCourseDetail } from "utils/course";

/**
 * Loads course detail and composes sidebar plus program, replay, and comment tabs.
 */
const Course = () => {
  const { t } = useTranslation("common");

  const { courseId } = useParams();
  const navigate = useNavigate();

  const [controller, dispatch] = useAsterController();
  const { courses, structure, trackRoutes, loadedCourseDetail, loadedDashboard, preview, sessionHydrated, firstLoadPending } = controller;

  const [tab, setTab] = useState(0);
  const [wrap, setWrap] = useState(true);
  const [continueLink, setContinueLink] = useState(null);
  const [continueExist, setContinueExist] = useState(false);

  useEffect(() => {
    if (!sessionHydrated || !loadedDashboard) return;
    preview && actionPreviewModeSet(dispatch, null);

    const currCourse = (courses.data || []).find((v) => v.id === courseId) || {};
    if (!currCourse.enrollment_status) navigate("/dashboard");
  }, [sessionHydrated, courseId, courses.data, loadedDashboard, navigate, dispatch, preview]);

  useEffect(() => {
    if (!shouldFetchCourseDetail(sessionHydrated, courseId, firstLoadPending)) return;
    actionAppCourseDetailGet(dispatch, courseId);
  }, [sessionHydrated, firstLoadPending, courseId, dispatch]);

  useEffect(() => {
    if (Object.keys(structure).length && trackRoutes) {
      const trackRoute = trackRoutes.find((t) => t["course_id"] === courseId);

      if (trackRoute) {
        // If there is previous link, then set it as continue link
        setContinueLink(`/content/${structure.course_id}/${trackRoute["url"]}`);
        setContinueExist(true);
      } else {
        // If there is no item, then we set the first block link
        if (!structure.sections) return;
        for (let i = 0; i < structure.sections.length; i++) {
          const section = structure.sections[i];
          if (!section.sequences) continue;
          for (let j = 0; j < section.sequences.length; j++) {
            const sequence = section.sequences[j];
            if (!sequence.verticals) continue;
            for (let k = 0; k < sequence.verticals.length; k++) {
              setContinueLink(`/content/${structure.course_id}/${i}/${j}/${k}`);
              return;
            }
          }
        }
      }
    }
  }, [structure, trackRoutes, courseId]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  return (
    <>
      <MuiDrawer anchor="left" open={!wrap} onClose={() => setWrap(!wrap)}>
        <Box className="w-60">
          <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${tab === 0 ? "bg-red-100 text-red-500" : ""}`} onClick={() => setTab(0)}>
            <ListItemIcon className="text-xl min-w-fit">
              <Icon icon="ic:sharp-menu-book" />
            </ListItemIcon>
            <ListItemText>
              <VText color="custom">{t("Program")}</VText>
            </ListItemText>
          </ListItemButton>
          <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${tab === 1 ? "bg-red-100 text-red-500" : ""}`} onClick={() => setTab(1)}>
            <ListItemIcon className="text-xl min-w-fit">
              <Icon icon="ic:outline-comment" />
            </ListItemIcon>
            <ListItemText>
              <VText color="custom">{t("Comments")}</VText>
            </ListItemText>
          </ListItemButton>
          <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${tab === 2 ? "bg-red-100 text-red-500" : ""}`} onClick={() => setTab(2)}>
            <ListItemIcon className="text-xl min-w-fit">
              <Icon icon="el:download" />
            </ListItemIcon>
            <ListItemText>
              <VText color="custom">{t("Download")}</VText>
            </ListItemText>
          </ListItemButton>
          <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${tab === 3 ? "bg-red-100 text-red-500" : ""}`} onClick={() => setTab(3)}>
            <ListItemIcon className="text-xl min-w-fit">
              <Icon icon="icon-park-outline:gift-bag" />
            </ListItemIcon>
            <ListItemText>
              <VText color="custom">{t("Bonus")}</VText>
            </ListItemText>
          </ListItemButton>
          <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${tab === 4 ? "bg-red-100 text-red-500" : ""}`} onClick={() => setTab(4)}>
            <ListItemIcon className="text-xl min-w-fit">
              <Icon icon="ic:outline-replay" />
            </ListItemIcon>
            <ListItemText>
              <VText color="custom">{t("Replay")}</VText>
            </ListItemText>
          </ListItemButton>
        </Box>
      </MuiDrawer>

      <Box className="m-0 md:m-6 v-bg-body rounded-none md:rounded-md flex-1">
        <Box className="mx-auto v-bg-body">
          <Box className="px-14 pt-0 md:pt-8">
            <Link to={`/dashboard`} className="no-underline hidden md:block w-max">
              <VButton color="secondary" className="text-lg">
                <Icon icon="bi:chevron-left" className="text-sm flex-shrink-0" />
                &nbsp;
                {t("Back")}
              </VButton>
            </Link>

            <Box className="hidden md:flex justify-between items-center pb-4 mt-4">
              {loadedCourseDetail ? (
                <VText className="text-4xl font-bold" div>
                  {structure.course_title}
                </VText>
              ) : (
                <Skeleton className="h-10" containerClassName="max-w-xs w-full" />
              )}
            </Box>

            <Box className="hidden md:flex flex-row-reverse flex-wrap justify-between items-center">
              {continueLink && (
                <div className="w-3/12 pl-6 h-10 flex justify-center">
                  <Link to={continueLink} className="no-underline relative w-60">
                    <VButton variant="contained" color="primary" className="absolute bottom-2 normal-case w-full py-2 text-limit-1 w-60">
                      {continueExist ? t("Continue") : t("Start Course")}
                    </VButton>
                  </Link>
                </div>
              )}

              <Box>
                <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue} className="items-end">
                  <Tab label={t("Program")} icon={<Icon className="mb-0 text-xl" icon="ic:sharp-menu-book" />} className="min-h-0 py-2 px-3 text-sm flex-row items-center gap-2 h-10 normal-case" />
                  <Tab label={t("Comments")} icon={<Icon className="mb-0 text-xl" icon="ic:outline-comment" />} className="min-h-0 py-2 px-3 text-sm flex-row items-center gap-2 h-10 normal-case" />
                  <Tab label={t("Download")} icon={<Icon className="mb-0 text-xl" icon="el:download" />} className="min-h-0 py-2 px-3 text-sm flex-row items-center gap-2 h-10 normal-case" />
                  <Tab label={t("Bonus")} icon={<Icon className="mb-0 text-xl" icon="icon-park-outline:gift-bag" />} className="min-h-0 py-2 px-3 text-sm flex-row items-center gap-2 h-10 normal-case" />
                  <Tab label={t("Replay")} icon={<Icon className="mb-0 text-xl" icon="ic:outline-replay" />} className="min-h-0 py-2 px-3 text-sm flex-row items-center gap-2 h-10 normal-case" />
                </Tabs>
              </Box>
            </Box>
          </Box>

          <Divider className="bg-gray-200 dark:bg-gray-500 hidden md:flex" />

          <Grid container spacing={2} className="px-3">
            <Grid item xs={12} className="flex md:hidden justify-between items-center">
              <VText className="border-solid border-0 border-b-2 border-primary font-bold flex items-center gap-1 py-1 text-neutral-700">
                {tab === 0 && (
                  <>
                    <Icon className="mb-0" icon="ic:sharp-menu-book" />
                    {t("Program")}
                  </>
                )}
                {tab === 1 && (
                  <>
                    <Icon className="mb-0" icon="ic:outline-comment" />
                    {t("Comments")}
                  </>
                )}
                {tab === 2 && (
                  <>
                    <Icon className="mb-0" icon="el:download" />
                    {t("Download")}
                  </>
                )}
                {tab === 3 && (
                  <>
                    <Icon className="mb-0" icon="icon-park-outline:gift-bag" />
                    {t("Bonus")}
                  </>
                )}
                {tab === 4 && (
                  <>
                    <Icon className="mb-0" icon="ic:outline-replay" />
                    {t("Replay")}
                  </>
                )}
              </VText>
              <IconButton component="label" onClick={() => actionMiniSidenav(dispatch, false)}>
                <Icon icon="heroicons-solid:menu-alt-1" />
              </IconButton>
            </Grid>
          </Grid>

          <Box className="px-4 md:px-14 md:mt-14 mt-5">
            <Grid container spacing={5}>
              <Grid item xs={12} lg={9}>
                {tab === 0 && <CourseProgram />}
                {tab === 1 && <CourseComments />}
                {tab === 2 && <CourseDownload />}
                {tab === 3 && <CourseBonus />}
                {tab === 4 && <CourseReplay />}
              </Grid>
              <CourseSidebar setParentTab={setTab} />
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Course;
